/**
 * Sidebar vertical avec menu groupé et dropdown
 */

import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS, api } from '@e-store/shared';
import { useEnterpriseBranding } from '../hooks/useEnterpriseBranding';
import { useTheme } from '../contexts/ThemeContext';
import type { ModuleGroupId, ModuleItemId, EnterpriseModulesConfig } from '@e-store/shared/types/modules';
import {
  ALL_MODULES_ENABLED,
  DEFAULT_MODULES_CONFIG,
  mergeModulesConfig,
} from '@e-store/shared/types/modules';
import './Sidebar.css';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  permission?: string;
  moduleId?: ModuleItemId;
}

interface NavGroup {
  label: string;
  icon: string;
  groupId: ModuleGroupId;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Mon Espace',
    icon: '👤',
    groupId: 'mon-espace',
    items: [
      { path: '/profile', label: 'Mon Profil', icon: '👤', moduleId: 'profile' },
      { path: '/my-salary', label: 'Mon Salaire', icon: '💰', moduleId: 'my-salary' },
    ],
  },
  {
    label: 'Ventes',
    icon: '📊',
    groupId: 'ventes',
    items: [
      { path: '/dashboard', label: 'Tableau de bord', icon: '🏠', moduleId: 'dashboard' },
      { path: '/orders', label: 'Commandes', icon: '🛒', moduleId: 'orders' },
      { path: '/invoices', label: 'Facturation', icon: '📄', moduleId: 'invoices' },
      { path: '/cash-register', label: 'Caisse', icon: '💵', moduleId: 'cash-register' },
      { path: '/sales-reports', label: 'Rapports', icon: '📈', moduleId: 'sales-reports' },
      { path: '/analytics', label: 'Traffic FrontOffice', icon: '📊', permission: 'admin', moduleId: 'analytics' },
    ],
  },
  {
    label: 'Comptabilité',
    icon: '📒',
    groupId: 'comptabilite',
    items: [
      { path: '/accounting', label: 'Rapports comptables', icon: '📊', permission: 'comptabilite:read', moduleId: 'accounting' },
      { path: '/employee-delays', label: 'Retards employés', icon: '⏰', permission: 'comptabilite:read', moduleId: 'employee-delays' },
      { path: '/salary-deductions', label: 'Prélèvements salaire', icon: '💳', permission: 'comptabilite:read', moduleId: 'salary-deductions' },
      { path: '/payroll', label: 'Calcul paie', icon: '💰', permission: 'comptabilite:read', moduleId: 'payroll' },
    ],
  },
  {
    label: 'Catalogue',
    icon: '🏷️',
    groupId: 'catalogue',
    items: [
      { path: '/articles', label: 'Articles', icon: '🏷️', moduleId: 'articles' },
      { path: '/articles/categories', label: 'Catégories', icon: '📂', moduleId: 'categories' },
      { path: '/reviews', label: 'Avis clients', icon: '⭐', permission: 'admin', moduleId: 'reviews' },
    ],
  },
  {
    label: 'Contacts',
    icon: '👥',
    groupId: 'contacts',
    items: [
      { path: '/contacts', label: 'Gestion des contacts', icon: '👥', moduleId: 'contacts' },
    ],
  },
  {
    label: 'Stock',
    icon: '📦',
    groupId: 'stock',
    items: [
      { path: '/stocks', label: 'Gestion stock', icon: '📦', moduleId: 'stocks' },
      { path: '/stock-arrivals', label: 'Arrivages', icon: '📥', moduleId: 'stock-arrivals' },
      { path: '/inventory', label: 'Inventaire', icon: '📋', moduleId: 'inventory' },
    ],
  },
  {
    label: 'Support',
    icon: '❓',
    groupId: 'support',
    items: [
      { path: '/faq', label: 'FAQ', icon: '❓', moduleId: 'faq' },
      { path: '/faq/management', label: 'Gérer FAQ', icon: '⚙️', permission: 'admin', moduleId: 'faq-management' },
    ],
  },
  {
    label: 'Administration',
    icon: '⚙️',
    groupId: 'administration',
    items: [
      { path: '/users', label: 'Utilisateurs', icon: '👥', permission: 'admin', moduleId: 'users' },
      { path: '/config/sites', label: 'Magasins', icon: '🏬', permission: 'admin', moduleId: 'sites' },
      { path: '/config/enterprise', label: 'Entreprise', icon: '🏢', permission: 'admin', moduleId: 'enterprise' },
      { path: '/history', label: 'Historique', icon: '📜', permission: 'admin', moduleId: 'history' },
    ],
  },
];

// Groupe Super Admin (visible uniquement pour super_admin)
const SUPER_ADMIN_GROUP: NavGroup = {
  label: 'Super Admin',
  icon: '🔐',
  groupId: 'administration', // Utilise administration comme fallback
  items: [
    { path: '/super-admin/enterprises', label: 'Entreprises', icon: '🏢' },
  ],
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

// Helper function to check if a nav item matches current location
function isNavItemActive(itemPath: string, locationPathname: string, locationSearch: string): boolean {
  // Parse item path to separate pathname and search
  const [itemPathname, itemSearch] = itemPath.split('?');

  // Check if pathnames match exactly OR if current path is a sub-route
  // e.g., /stocks should match /stocks, /stocks/history, /stocks/movements
  const isPathMatch = locationPathname === itemPathname ||
    (locationPathname.startsWith(itemPathname + '/') && itemPathname !== '/');

  if (!isPathMatch) {
    return false;
  }

  // If item has query params, check if they match in location
  if (itemSearch) {
    const itemParams = new URLSearchParams(itemSearch);
    const locationParams = new URLSearchParams(locationSearch);

    // Check each param in item exists in location
    for (const [key, value] of itemParams) {
      if (locationParams.get(key) !== value) {
        return false;
      }
    }
  }

  return true;
}

export function Sidebar({ isOpen, onClose, collapsed, onCollapsedChange }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { enterpriseName } = useEnterpriseBranding();
  const { theme, toggleTheme } = useTheme();

  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    // Initialiser avec le groupe actif
    const activeGroup = NAV_GROUPS.find((group) =>
      group.items.some((item) => isNavItemActive(item.path, location.pathname, location.search))
    );
    return activeGroup ? [activeGroup.label] : [];
  });

  const [isHovered, setIsHovered] = useState(false);
  const [modulesConfig, setModulesConfig] = useState<EnterpriseModulesConfig>(DEFAULT_MODULES_CONFIG);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Charger la configuration des modules
  useEffect(() => {
    const loadModulesConfig = async () => {
      // Super admin voit tout, y compris les modules ajoutés au défaut à `false`
      if (user?.role === 'super_admin') {
        setModulesConfig(ALL_MODULES_ENABLED);
        return;
      }

      try {
        const response = await api.get<Partial<EnterpriseModulesConfig>>('/api/enterprise/modules');
        // Merge conservé côté client : il protège la fenêtre de déploiement (bundle
        // CloudFront en avance/retard sur la Lambda) et la divergence de clés.
        setModulesConfig(mergeModulesConfig(response.data));
      } catch (error) {
        console.error('Erreur chargement config modules:', error);
        // En cas d'erreur, utiliser la config par défaut (tout activé)
        setModulesConfig(DEFAULT_MODULES_CONFIG);
      }
    };

    if (user) {
      loadModulesConfig();
    }
  }, [user]);

  // Charger le nombre de commandes en attente
  useEffect(() => {
    const loadPendingOrders = async () => {
      try {
        const response = await api.get<{ orders: unknown[]; total: number }>('/api/orders?status=pending&pageSize=1');
        if (response.data) {
          setPendingOrdersCount(response.data.total || 0);
        }
      } catch {
        // Silencieux - ne pas bloquer le sidebar
      }
    };

    if (user) {
      loadPendingOrders();
      // Rafraichir toutes les 2 minutes
      const interval = setInterval(loadPendingOrders, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Helper pour vérifier si un module est activé
  const isModuleEnabled = (groupId: ModuleGroupId, moduleId?: ModuleItemId): boolean => {
    // Super admin voit tout
    if (user?.role === 'super_admin') return true;

    // Vérifier le groupe d'abord
    if (!modulesConfig.groups[groupId]) return false;

    // Si moduleId fourni, vérifier l'item
    if (moduleId && !modulesConfig.items[moduleId]) return false;

    return true;
  };

  // Helper pour vérifier les permissions de rôle
  const hasRolePermission = (item: NavItem): boolean => {
    if (!item.permission) return true;
    if (item.permission === 'admin') return user?.role === 'admin' || user?.role === 'super_admin';
    // Admin, super_admin et comptable ont accès aux pages comptabilité
    if (item.permission === 'comptabilite:read') {
      return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'comptable';
    }
    return user?.permissions?.includes(item.permission) || false;
  };

  // Filtrer les groupes selon les modules activés ET les permissions
  let filteredGroups = NAV_GROUPS
    .filter((group) => isModuleEnabled(group.groupId)) // Filtrer par groupe de modules
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        // Vérifier si le module item est activé
        if (!isModuleEnabled(group.groupId, item.moduleId)) return false;
        // Vérifier les permissions de rôle
        return hasRolePermission(item);
      }),
    }))
    .filter((group) => group.items.length > 0);

  // Ajouter le groupe Super Admin pour les super_admin
  if (user?.role === 'super_admin') {
    filteredGroups = [...filteredGroups, SUPER_ADMIN_GROUP];
  }

  return (
    <>
      {/* Overlay pour mobile */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-label="Fermer le menu"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? 'open' : ''} ${collapsed && !isHovered ? 'collapsed' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <h1>{enterpriseName}</h1>
          </div>
          <div className="sidebar-header-actions">
            <button
              className="sidebar-collapse-btn"
              onClick={() => onCollapsedChange(!collapsed)}
              aria-label={collapsed ? "Agrandir le menu" : "Réduire le menu"}
              title={collapsed ? "Agrandir le menu" : "Réduire le menu"}
            >
              {collapsed ? '▶' : '◀'}
            </button>
            <button
              className="sidebar-close"
              onClick={onClose}
              aria-label="Fermer le menu"
            >
              ✕
            </button>
          </div>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="sidebar-avatar">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="Avatar" />
            ) : (
              <>{user?.firstName?.[0]}{user?.lastName?.[0]}</>
            )}
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">
              {user?.lastName?.toUpperCase()} {user?.firstName}
            </span>
            <span className="sidebar-user-role">
              {user?.role && ROLE_LABELS[user.role]}
            </span>
          </div>
        </div>

        {/* Navigation Groups */}
        <nav className="sidebar-nav">
          {filteredGroups.map((group) => {
            const isExpanded = expandedGroups.includes(group.label);
            const isActiveGroup = group.items.some(
              (item) => isNavItemActive(item.path, location.pathname, location.search)
            );

            return (
              <div key={group.label} className="nav-group">
                <button
                  className={`nav-group-header ${isActiveGroup ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleGroup(group.label)}
                >
                  <span className="nav-group-icon">{group.icon}</span>
                  <span className="nav-group-label">{group.label}</span>
                  <span className="nav-group-arrow">{isExpanded ? '▼' : '▶'}</span>
                </button>

                {isExpanded && (
                  <div className="nav-group-items">
                    {group.items.map((item) => {
                      const isActive = isNavItemActive(item.path, location.pathname, location.search);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`nav-item ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            if (window.innerWidth < 1024) {
                              onClose();
                            }
                          }}
                        >
                          <span className="nav-item-icon">{item.icon}</span>
                          <span className="nav-item-label">{item.label}</span>
                          {item.path === '/orders' && pendingOrdersCount > 0 && (
                            <span className="nav-item-badge">{pendingOrdersCount}</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer Actions */}
        <div className="sidebar-footer">
          <button
            className="sidebar-theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Activer le mode sombre' : 'Activer le mode clair'}
            title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}
          >
            <span>{theme === 'light' ? '🌙' : '☀️'}</span>
            <span>{theme === 'light' ? 'Mode sombre' : 'Mode clair'}</span>
          </button>
          <button className="sidebar-logout" onClick={handleLogout}>
            <span>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
}
